import React, { useEffect, useCallback } from 'react';
import TextElement from './TextElement';
import SvgEditorReusable from './SvgEditorReusable';
import ReusableBarcodeEditor from './ReusableBarcodeEditor';
import ImageEditor from './ImageEditor';
import FillableElementRenderer from './FillableElementRenderer';
import { Chart } from './Charts';
import {
  TEXT_ELEMENT,
  IMAGE_ELEMENT,
  SVG_ELEMENT,
  BARCODE_ELEMENT,
  FILLABLE_ELEMENT,
  STATIC_TABLE_ELEMENT,
  ELEMENT_TYPES,
  HEADER,
  FOOTER,
  dummyCallback,
} from '../../../utils/complexDocumentConstants';
import CreativeTableEditor from './CreativeTableEditor';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../../context/complexDocumentContext';

const BlockElementRenderer = ({ element, editable, selected, scaling, pageId, dataSetKey, dataIndex, updateSingleElement, isMobileView = false, ...props }) => {
  const { adjustScroll, creativeBlock, staticTableBlock, refreshSelectedElementIds, parentStoredElements = [],
    updateParentComplexElementDetails = dummyCallback, parentUUID, onCellDoubleClick = dummyCallback,
    updateElementRect = dummyCallback, elementIndex } = props;
  let elementStyle = Object.assign({}, element.style, {
    transform: `translateX(${element.style.x}px) translateY(${element.style.y}px)` +
        ` rotate(${element.style.rotate || 0}deg)`,
    position: staticTableBlock ? 'static' : 'absolute',
    width: `${element.style.width}${staticTableBlock ? '%' : 'px'}`,
    height: staticTableBlock && (element.contentType === BARCODE_ELEMENT || element.contentType === IMAGE_ELEMENT) ? 'auto' : `${element.style.height}${staticTableBlock && !['line', 'doubleLine'].includes(element.uiType) ? '%' : 'px'}`,
    background: element.style.backgroundColor,
  });

  const highlightIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_HIGHLIGHT_ID], []);
  const [highlightId, store] = useDocumentContext(highlightIdSelector);


  if (isMobileView) {
    elementStyle = Object.assign({}, elementStyle, {
    transform: `translateX(${element.style.x * 0.66}px) translateY(${element.style.y * 0.66}px)` +
        ` rotate(${element.style.rotate}deg) scale(0.66)`,
    });
  }
  delete elementStyle.x;
  delete elementStyle.y;

  useEffect(() => {
    const blockElement = document.getElementById(creativeBlock?.uuid);
    const highlightGroupElements = e => {
      store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_HIGHLIGHT_ID]: e.detail.groupId });
    };
    if (blockElement) {
      blockElement.addEventListener('focusGroupElement', highlightGroupElements);
    }
    return () => {
      if (blockElement) {
        blockElement.removeEventListener('focusGroupElement', highlightGroupElements);
      }
    };
  }, []);

  if (element.contentType === TEXT_ELEMENT) {
    return (
      <div
        style={{...elementStyle, zIndex: selected && 2, ...(staticTableBlock && { width: '100%' })}}
        data-uuid={element.uuid}
        data-group-uuid={element.groupId}
        className={`selectable ${element.groupId === highlightId ? 'highlight' : ''}`}
        data-element-type={element.contentType}
        data-element-index={elementIndex}
      >
        <TextElement
          element={element}
          editable={editable}
          selected={selected}
          scaling={scaling}
          pageId={pageId}
          adjustScroll={adjustScroll}
          isStandalone= {creativeBlock?.isStandalone}
          isHeaderEelement = {creativeBlock?.type === HEADER || creativeBlock?.type === FOOTER}
          isFromCreativeBlock={!staticTableBlock}
        />
      </div>
    )
  } else if (element.contentType === IMAGE_ELEMENT) {
    return (
      <div
        style={elementStyle}
        data-uuid={element.uuid}
        data-group-uuid={element.groupId}
        className={`selectable ${element.groupId === highlightId ? 'highlight' : ''}`}
        data-element-type={
          element.uiType !== undefined
            ? element.uiType
            : element.contentType
        }
        data-element-index={elementIndex}
      >
        <ImageEditor
          element={element}
          editable={editable}
          selected={selected}
          dataSetKey={dataSetKey}
          dataIndex={dataIndex}
          isFromCreativeBlock={!staticTableBlock}
        />
      </div>
    );
  } else if (element.contentType === SVG_ELEMENT) {
    return (
      <div
        style={elementStyle}
        data-uuid={element.uuid}
        data-group-uuid={element.groupId}
        className={`selectable ${element.groupId === highlightId ? 'highlight' : ''}`}
        data-element-type={
          element.uiType !== undefined
            ? element.uiType
            : element.contentType
        }
        data-element-index={elementIndex}
      >
        <SvgEditorReusable
          uuid={element.uuid}
          element={element}
          styles={element.uiType === 'rectangle'
            ? { width: element.styles?.width, height: element.styles?.height }
            : {}}
          editable={editable}
          selected={selected}
          isFromCreativeBlock={!staticTableBlock}
        />
      </div>
    )
  } else if (element.contentType === BARCODE_ELEMENT) {
    return (
      <div
        style={elementStyle}
        data-uuid={element.uuid}
        data-group-uuid={element.groupId}
        className={`selectable ${element.groupId === highlightId ? 'highlight' : ''}`}
        data-element-type={element.contentType}
        data-element-index={elementIndex}
      >
        <ReusableBarcodeEditor
          element={element}
          editable={editable}
          selected={selected}
          isFromCreativeBlock={!staticTableBlock}
        />
      </div>
    );
  } else if (element.contentType === FILLABLE_ELEMENT) {
    return (
      <FillableElementRenderer
        element={element}
        editable={editable}
        selected={selected}
        scaling={scaling}
        isFromCreativeBlock={!staticTableBlock}
        staticTableBlock={staticTableBlock}
        refreshSelectedElementIds={refreshSelectedElementIds}
        parentStoredElements={parentStoredElements}
        updateParentComplexElementDetails={updateParentComplexElementDetails}
        parentUUID={parentUUID}
        onCellDoubleClick={onCellDoubleClick}
        updateElementRect={updateElementRect}
        elementIndex={elementIndex}
        highlightId={highlightId}
      />
    );
  } else if (element.contentType === STATIC_TABLE_ELEMENT) {
    return (
      <div
        style={elementStyle}
        data-uuid={element.uuid}
        data-group-uuid={element.groupId}
        className={`selectable ${element.groupId === highlightId ? 'highlight' : ''}`}
        data-element-type={element.contentType}
        data-element-index={elementIndex}
      >
        <CreativeTableEditor
          element={element}
          editable={editable}
          selected={selected}
          scaling={scaling}
          pageId={pageId}
          adjustScroll={adjustScroll}
          updateSingleElement={updateSingleElement}
          creativeBlock= {creativeBlock}
        />
      </div>

    )
  } else if (element.contentType === ELEMENT_TYPES.CHART) {
    return (
      <div
        style={elementStyle}
        data-uuid={element.uuid}
        data-group-uuid={element.groupId}
        className={`selectable ${element.groupId === highlightId ? 'highlight' : ''}`}
        data-element-type={element.contentType}
        data-element-index={elementIndex}
      >
        <Chart
          element={element}
          selected={selected}
          creativeBlock={creativeBlock}
        />
      </div>
    );
  }
  return (
    <div />
  )
}

export default React.memo(BlockElementRenderer);